import { useCallback, useRef } from 'react';
import type { User } from '@supabase/supabase-js';
import { toast } from 'sonner';
import * as orderService from '@/supabase/order/service/orderService';
import { logError } from '@/lib/sentry';
import { CustomerTemplate } from '@/components/CustomerTemplateManager';
import type { PaymentMethod } from '@/supabase/order/types/order';

interface UsePaymentMethodActionsProps {
  user: User | null;
  customerTemplates: CustomerTemplate[];
  setLocalDateOrderTemplates: (
    updater: CustomerTemplate[] | ((prev: CustomerTemplate[]) => CustomerTemplate[])
  ) => void;
  extractOrderId: (customerId: string) => number | null;
}

interface UsePaymentMethodActionsResult {
  /** pill 토글 변경. 같은 값 재클릭 = null(미기재). order_ 고객 전제(UI에서 게이트) */
  handlePaymentMethodChange: (customerId: string, next: PaymentMethod | null) => void;
}

/**
 * 결제수단(현금/카드) 기재 도메인 훅.
 * 옵티미스틱 업데이트 + seq 가드 롤백(최신 요청만 롤백 — 마지막 쓰기 승리, 스펙 §3.3).
 * setLocalDateOrderTemplates는 Home 소유 live 참조 주입(snapshot 금지).
 * customerTemplates는 committed-state 스냅샷 prop(Home 렌더마다 최신값 주입, useOrderStatusActions 선례).
 */
export function usePaymentMethodActions({
  user,
  customerTemplates,
  setLocalDateOrderTemplates,
  extractOrderId,
}: UsePaymentMethodActionsProps): UsePaymentMethodActionsResult {
  // 고객별 마지막 요청 seq — 최신 요청만 롤백 권한을 가짐 (값 비교의 ABA 함정 회피)
  const requestSeqRef = useRef<Record<string, number>>({});
  // 고객별 저장 직렬화 체인 — 호출 순서 = 서버 기록 순서 (성공 순서 역전 시 DB stale 차단)
  const saveChainRef = useRef<Record<string, Promise<void>>>({});
  // 고객별 마지막 서버 확정값 — 롤백 기준 (prevValue는 큐잉된 멀티실패 시 미확정 옵티미스틱 값일 수 있음)
  const lastCommittedRef = useRef<Record<string, PaymentMethod | null>>({});

  const handlePaymentMethodChange = useCallback(
    (customerId: string, next: PaymentMethod | null) => {
      if (!user) return;
      if (!customerId.startsWith('order_')) return;
      const orderId = extractOrderId(customerId);
      if (!orderId) return;

      // 첫 사용 시점의 customerTemplates 값은 서버 로드값(클릭 전 in-flight 없음) — 이후엔 성공 시점마다 갱신
      if (!(customerId in lastCommittedRef.current)) {
        const target = customerTemplates.find(cust => cust.id === customerId);
        lastCommittedRef.current[customerId] = target?.paymentMethod ?? null;
      }
      const seq = (requestSeqRef.current[customerId] = (requestSeqRef.current[customerId] ?? 0) + 1);
      // 옵티미스틱 반영 (updater는 순수 — prevValue는 committed state에서 캡처)
      setLocalDateOrderTemplates(prev =>
        prev.map(cust =>
          cust.id === customerId ? ({ ...cust, paymentMethod: next } as CustomerTemplate) : cust
        )
      );

      const prevChain = saveChainRef.current[customerId] ?? Promise.resolve();
      const attempt = prevChain
        .catch(() => {})
        .then(() => orderService.updateOrderPaymentMethod(orderId, next));
      saveChainRef.current[customerId] = attempt.then(() => undefined, () => undefined);
      attempt.then(
        () => {
          lastCommittedRef.current[customerId] = next;
        },
        err => {
          logError(`[usePaymentMethodActions] 결제수단 업데이트 실패 (orderId ${orderId}):`, err);
          // seq 가드: 이 요청이 해당 고객의 최신 요청일 때만 toast+롤백 (마지막 쓰기 승리).
          // stale 실패는 사용자에게 침묵 — 최종 상태는 정상이므로. logError는 항상 실행.
          // 롤백 기준은 마지막 서버 확정값 — prevValue(옵티미스틱일 수 있음) 아님 [codex]
          if (seq === requestSeqRef.current[customerId]) {
            toast.error('결제수단 저장에 실패했습니다.');
            const committed = lastCommittedRef.current[customerId] ?? null;
            setLocalDateOrderTemplates(prev =>
              prev.map(cust =>
                cust.id === customerId ? ({ ...cust, paymentMethod: committed } as CustomerTemplate) : cust
              )
            );
          }
        }
      );
    },
    [user, customerTemplates, setLocalDateOrderTemplates, extractOrderId],
  );

  return { handlePaymentMethodChange };
}
